/**
 * safeUuid — RFC4122 v4 UUID generator with a Math.random fallback.
 *
 * crypto.randomUUID() shipped on iOS Safari 15.4 (March 2022). On older iOS
 * the call throws at runtime, which crashes any hook that calls it during render.
 * This wrapper falls back to a Math.random-based v4 UUID — suitable for
 * client-side correlation IDs (contextId, requestId) where cryptographic
 * randomness is not required.
 */
export function safeUuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    // RFC4122 v4 via Math.random — fine for client-side correlation IDs (not crypto)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

/**
 * slotRequestId — the pack-generation idempotency key, STABLE ACROSS RETRIES of a slot.
 *
 * Keyed on (launchToken, slotIndex). launchToken is minted once per generation run
 * (ENTITLEMENT_READY) and embeds the grantId, so the key is unique per (grant, run, slot) and
 * IDENTICAL across the retry attempts of one slot. That stability is the whole point: it makes the
 * server's (grantId, requestId) unique constraint absorb a retry that follows a crash-after-consume,
 * instead of minting a second consumption. A fresh uuid per attempt double-consumes — see
 * reports/2026_07_11_database_assessment.md V2. Do not replace this with safeUuid().
 */
export function slotRequestId(launchToken: string, slotIndex: number): string {
    return `${launchToken}:slot${slotIndex}`
}
